import { useEffect, useState } from 'react';
import { BarChart2, TrendingUp, ShoppingCart, Package } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { analyticsAPI } from '../api';

export default function AnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [statsRes, revenueRes, bestRes] = await Promise.all([
          analyticsAPI.getStats(),
          analyticsAPI.getRevenue(year),
          analyticsAPI.getBestSellers(),
        ]);
        setStats(statsRes.data.stats);
        setRevenueData(revenueRes.data.data);
        setBestSellers(bestRes.data.data);
      } catch (e) {
        setStats({ totalProducts: 0, totalOrders: 0, totalRevenue: 0, pendingOrders: 0 });
        setRevenueData(Array.from({ length: 12 }, (_, i) => ({
          month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
          revenue: 0, orders: 0,
        })));
      } finally { setLoading(false); }
    };
    fetch();
  }, [year]);

  const summaryCards = [
    { label: 'Delivered Amount', value: `₹${(stats?.totalRevenue || 0).toLocaleString()}`, icon: TrendingUp, color: '#14b8a6', bg: '#f0fdfa' },
    { label: 'Total Orders', value: stats?.totalOrders || 0, icon: ShoppingCart, color: '#667eea', bg: '#f5f3ff' },
    { label: 'Total Products', value: stats?.totalProducts || 0, icon: Package, color: '#f59e0b', bg: '#fffbeb' },
    { label: 'Pending Orders', value: stats?.pendingOrders || 0, icon: BarChart2, color: '#ef4444', bg: '#fef2f2' },
  ];

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">Sales performance and insights</p>
        </div>
        <select className="form-select" value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
          {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {summaryCards.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} style={{ background: bg, border: `1px solid ${color}22`, borderRadius: '16px', padding: '22px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ width: '48px', height: '48px', background: `${color}22`, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={24} color={color} />
            </div>
            <div>
              <div style={{ fontSize: '22px', fontWeight: '800', color }}>{value}</div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        {/* Revenue */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '20px' }}>📈 Monthly Revenue ({year})</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px' }} formatter={v => [`₹${v.toLocaleString()}`, 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke="#14b8a6" strokeWidth={2.5} fill="url(#areaGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders */}
        <div className="glass-card" style={{ padding: '24px' }}>
          <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '20px' }}>📦 Monthly Orders ({year})</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenueData} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Bar dataKey="orders" fill="#667eea" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Best Sellers */}
      <div className="glass-card" style={{ padding: '24px' }}>
        <h3 style={{ fontWeight: '700', fontSize: '15px', marginBottom: '20px' }}>🏆 Best Selling Products</h3>
        {bestSellers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
            <Package size={36} style={{ margin: '0 auto 12px' }} />
            <p>No sales data yet. Best sellers will appear here once you have paid orders.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {bestSellers.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px', background: '#f8fafc', borderRadius: '12px' }}>
                <span style={{ width: '28px', height: '28px', background: i < 3 ? ['#ffd700', '#c0c0c0', '#cd7f32'][i] : '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '12px', flexShrink: 0 }}>
                  {i + 1}
                </span>
                {p.image && <img src={p.image} alt="" style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover' }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#0f172a' }}>{p.productName || 'Product'}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{p.totalQuantity} units sold</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '700', color: '#14b8a6' }}>₹{p.totalRevenue?.toLocaleString()}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>revenue</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
